/**
 * 誤って作った会社の削除（#185、docs/74 §6。2026-10-14 射場さんの決定）。設定のメニューの一番下で「退会」の代わりに出す。
 *   出すのは：作った本人（管理者）・無料トライアル中・データが空（顧客・案件・帳票が 0、ユーザーが本人だけ）・ほかに使える同じ製品の会社がある
 *   ＝API の GET /company/delete-check が deletable のときだけ。満たさないときはボタンを出さず、今の「退会」（30日の猶予）を出す。
 *   押すと確認の窓（題「会社削除」・「「○○」を削除します。元に戻せません。」・取消／削除（朱））→ POST /company/delete →
 *   ログインはほかの会社へ移り（返る Me を入れる）、今日の画面に「「○○」を削除しました」
 */
import { COMPANY_DELETE_MSG, type CompanyDeleteState, type Me } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { api, messageOf } from "../api.js";
import { setMe } from "../auth.js";
import { ErrorText } from "./ui.jsx";

export const COMPANY_DELETE_KEY = ["company", "delete-check"] as const;

/** 消せるか（管理者で、出す場所があるときだけ問い合わせる） */
export function useCompanyDeletable(enabled: boolean): CompanyDeleteState | undefined {
  const q = useQuery({ queryKey: COMPANY_DELETE_KEY, queryFn: () => api<CompanyDeleteState>("GET", "/company/delete-check"), enabled, staleTime: 0 });
  return enabled ? q.data : undefined;
}

export function CompanyDeleteDialog({ name, onClose }: { name: string; onClose: () => void }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const del = useMutation({
    mutationFn: () => api<Me & { deletedName?: string }>("POST", "/company/delete", {}),
    onSuccess: async (m) => {
      // 会社の切替と同じ形（ME を入れ替えて、ほかの問い合わせは読み直す）
      await setMe(qc, m);
      void qc.invalidateQueries();
      nav("/home", { replace: true, state: { notice: COMPANY_DELETE_MSG.done(m.deletedName || name) } });
    },
    onError: () => void qc.invalidateQueries({ queryKey: COMPANY_DELETE_KEY }),
  });
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !del.isPending) onClose();
    };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose, del.isPending]);
  // body の直下に描く（PC の設定は左の列の中から開く＝列の中に描くと右の画面の下に隠れる）
  return createPortal(
    <div className="dim" onClick={(e) => e.target === e.currentTarget && !del.isPending && onClose()}>
      <div className="dlg codel" role="alertdialog" aria-modal="true" aria-labelledby="codel-h" aria-describedby="codel-b" data-testid="company-delete-dialog">
        <div className="dh" id="codel-h">
          {COMPANY_DELETE_MSG.title}
        </div>
        <div className="db">
          <p id="codel-b" style={{ margin: 0 }}>
            {COMPANY_DELETE_MSG.body(name)}
          </p>
          <ErrorText>{del.isError ? messageOf(del.error) : undefined}</ErrorText>
        </div>
        <div className="df">
          <span className="sp" />
          <button type="button" className="btn text" autoFocus disabled={del.isPending} onClick={onClose} data-testid="company-delete-cancel">
            {COMPANY_DELETE_MSG.cancel}
          </button>
          <button type="button" className="btn shu" disabled={del.isPending} onClick={() => del.mutate()} data-testid="company-delete-submit">
            {COMPANY_DELETE_MSG.submit}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
